import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "";
const AD_INTERVAL = 7000;

// ── Placeholder animé quand aucune pub ──────────────────────────────────────
function AdPlaceholder({ variant = "horizontal", onShowForm }) {
  const variants = {
    horizontal: {
      wrapper: "flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed border-sensoria-accent/20 bg-gradient-to-r from-sensoria-accent/5 to-purple-900/10 px-5 py-4 transition-all hover:border-sensoria-accent/40 hover:bg-sensoria-accent/8",
      icon: "text-3xl",
      titleClass: "text-sm font-semibold text-sensoria-text",
      subClass: "text-xs text-sensoria-muted mt-0.5",
      cta: "ml-auto shrink-0 sensoria-btn px-4 py-1.5 text-xs",
    },
    square: {
      wrapper: "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-sensoria-accent/20 bg-gradient-to-b from-sensoria-accent/5 to-purple-900/10 p-6 text-center transition-all hover:border-sensoria-accent/40 hover:bg-sensoria-accent/8 aspect-square",
      icon: "text-4xl",
      titleClass: "text-sm font-semibold text-sensoria-text",
      subClass: "text-[11px] text-sensoria-muted",
      cta: "rounded-lg bg-sensoria-accent/15 border border-sensoria-accent/30 px-4 py-1.5 text-xs font-semibold text-sensoria-accent-light hover:bg-sensoria-accent/25 transition-all",
    },
    sidebar: {
      wrapper: "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-sensoria-accent/20 bg-gradient-to-b from-sensoria-accent/5 to-purple-900/10 p-5 text-center transition-all hover:border-sensoria-accent/40",
      icon: "text-3xl",
      titleClass: "text-xs font-semibold text-sensoria-text",
      subClass: "text-[10px] text-sensoria-muted leading-relaxed",
      cta: "rounded-lg bg-sensoria-accent/15 border border-sensoria-accent/30 px-3 py-1 text-[11px] font-semibold text-sensoria-accent-light hover:bg-sensoria-accent/25 transition-all",
    },
  };
  const v = variants[variant] || variants.horizontal;

  // Animation pulsante sur le fond
  return (
    <div className="relative overflow-hidden">
      {/* Glow animé en arrière-plan */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl">
        <div className="absolute -inset-4 animate-[pulse_4s_ease-in-out_infinite] rounded-full bg-sensoria-accent/5 blur-2xl" />
      </div>
      <div className={v.wrapper} onClick={onShowForm}>
        <span className={v.icon}>📢</span>
        <div className={variant === "horizontal" ? "min-w-0 flex-1" : ""}>
          <p className={v.titleClass}>Votre pub ici</p>
          <p className={v.subClass}>
            {variant === "horizontal"
              ? "4,99 € / 30 jours · Visible par toute la communauté"
              : "À partir de 4,99 €\npour 30 jours"}
          </p>
        </div>
        <button type="button" className={v.cta} onClick={onShowForm}>
          📢 Pub ici →
        </button>
      </div>
    </div>
  );
}

// ── Encart actif ────────────────────────────────────────────────────────────
function AdCard({ ad, ads, index, onShowForm, variant = "horizontal" }) {
  if (variant === "square" || variant === "sidebar") {
    return (
      <div className="group relative overflow-hidden rounded-2xl">
        <a
          href={ad.link_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          {ad.image_url
            ? <div className={`overflow-hidden rounded-xl border border-sensoria-border/20 bg-sensoria-surface/60 ${variant === "square" ? "aspect-square" : "aspect-[4/3]"}`}>
                <img
                  src={ad.image_url}
                  alt={ad.title}
                  className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                />
              </div>
            : <div className={`flex items-center justify-center rounded-xl bg-sensoria-accent/10 ${variant === "square" ? "aspect-square" : "aspect-[4/3]"}`}>
                <span className="text-4xl">📢</span>
              </div>
          }
          <div className="mt-2 px-1">
            <span className="text-[9px] font-bold uppercase tracking-widest text-sensoria-accent/60">Pub</span>
            <p className="text-xs font-semibold text-sensoria-text line-clamp-2 leading-snug">{ad.title}</p>
            {ad.description && <p className="text-[10px] text-sensoria-muted line-clamp-3 mt-0.5 leading-relaxed">{ad.description}</p>}
          </div>
        </a>
        {ads.length > 1 && (
          <div className="mt-2 flex justify-center gap-1">
            {ads.map((_, i) => (
              <span key={i} className={`inline-block h-1 rounded-full transition-all ${i === index ? "w-4 bg-sensoria-accent" : "w-1 bg-sensoria-border"}`} />
            ))}
          </div>
        )}
        <button onClick={onShowForm} className="mt-2 block w-full rounded-lg bg-sensoria-accent/15 border border-sensoria-accent/30 py-1.5 text-center text-xs font-semibold text-sensoria-accent-light hover:bg-sensoria-accent/25 transition-all">
          📢 Diffuser ma pub ici →
        </button>
      </div>
    );
  }

  // horizontal (default)
  return (
    <div className="group relative w-full overflow-hidden rounded-2xl border border-sensoria-border/40 bg-sensoria-card transition-all hover:border-sensoria-accent/30">
      <div className="flex min-w-0 items-stretch overflow-hidden">
        {/* Image carrée */}
        {ad.image_url && (
          <div className="h-16 w-16 flex-shrink-0 overflow-hidden sm:h-20 sm:w-20">
            <img
              src={ad.image_url}
              alt={ad.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        )}
        {/* Texte */}
        <div className="flex min-w-0 flex-1 flex-col justify-center px-3 py-2 sm:px-4 sm:py-3">
          <span className="mb-0.5 text-[9px] font-bold uppercase tracking-widest text-sensoria-accent/60">Publicité</span>
          <p className="truncate text-xs font-semibold text-sensoria-text sm:text-sm">{ad.title}</p>
          {ad.description && <p className="line-clamp-1 text-[10px] text-sensoria-text-dim mt-0.5 sm:text-xs">{ad.description}</p>}
        </div>
        {/* Boutons à droite, empilés */}
        <div className="flex flex-shrink-0 flex-col items-end justify-center gap-1.5 pr-3 py-2 sm:gap-2 sm:pr-4 sm:py-3">
          <a
            href={ad.link_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-sensoria-accent/10 px-2.5 py-1 text-[10px] font-semibold text-sensoria-accent-light group-hover:bg-sensoria-accent/20 transition-all whitespace-nowrap sm:px-3 sm:py-1.5 sm:text-xs"
          >
            Voir →
          </a>
          <button onClick={onShowForm} className="rounded-lg bg-sensoria-accent/15 border border-sensoria-accent/30 px-2.5 py-1 text-[10px] font-semibold text-sensoria-accent-light hover:bg-sensoria-accent/25 transition-all whitespace-nowrap sm:px-3 sm:text-[11px]">
            📢 Pub ici →
          </button>
        </div>
      </div>
      {ads.length > 1 && (
        <div className="flex justify-center gap-1 pb-1.5">
          {ads.map((_, i) => (
            <span key={i} className={`inline-block h-1 rounded-full transition-all ${i === index ? "w-3 bg-sensoria-accent" : "w-1 bg-sensoria-border"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Formulaire de commande ───────────────────────────────────────────────────
function AdCheckoutForm({ token, onClose }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [link, setLink] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const uploadImg = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("entity_type", "ad_image");
    fd.append("entity_id", "shared");
    try {
      const r = await fetch(`${API}/api/photos/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok && (data.url || data.photo?.url)) setImgUrl(data.url || data.photo?.url);
      else setErr("Erreur upload image.");
    } catch { setErr("Erreur upload image."); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !imgUrl) { setErr("Titre et image requis."); return; }
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/ads/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title, description: desc, image_url: imgUrl, link_url: link }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Erreur");
      window.location.href = data.checkout_url;
    } catch (e) { setErr(e.message); setLoading(false); }
  };

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-sensoria-accent/20 bg-sensoria-card shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-sensoria-border/30 px-5 py-4">
        <div>
          <h3 className="font-semibold text-white">Commander un encart publicitaire</h3>
          <p className="text-xs text-sensoria-muted mt-0.5">Visible par tous les membres · paiement sécurisé Stripe</p>
        </div>
        <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-sensoria-muted hover:bg-sensoria-surface hover:text-white transition-all">✕</button>
      </div>

      {/* Tarif unique */}
      <div className="flex items-center gap-4 border-b border-sensoria-border/30 px-5 py-3">
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold text-sensoria-accent-light">4,99 €</span>
          <span className="text-xs text-sensoria-muted">/ 30 jours</span>
        </div>
        <p className="text-xs text-sensoria-muted">Visible par tous les membres · renouvelable à l'expiration</p>
      </div>

      <form onSubmit={submit} className="space-y-3 px-5 py-4">
        {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">⚠ {err}</p>}
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Titre de l'annonce *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex : Club Échangiste Paris 17e" className="sensoria-input w-full text-sm" required maxLength={80} />
        </div>
        <div>
          <label className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">
            <span>Description</span>
            <span className={desc.length > 180 ? "text-amber-400" : "text-sensoria-border"}>{desc.length}/200</span>
          </label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Courte description de votre offre…" rows={2} className="sensoria-input w-full resize-none text-sm" maxLength={200} />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Lien de destination</label>
          <input value={link} onChange={e => setLink(e.target.value)} placeholder="https://votre-site.fr" className="sensoria-input w-full text-sm" type="url" />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-sensoria-muted">Image * <span className="normal-case font-normal text-sensoria-muted">(recommandé : 800×400 px)</span></label>
          <div className="flex items-center gap-3">
            {imgUrl
              ? <img src={imgUrl} alt="" className="h-16 w-24 rounded-lg object-cover border border-sensoria-border/40" />
              : <div className="flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-sensoria-border/40 bg-sensoria-surface/30 text-sensoria-muted text-xs">Aperçu</div>
            }
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="rounded-lg border border-sensoria-border/40 px-4 py-2 text-xs text-sensoria-text-dim hover:border-sensoria-accent hover:text-sensoria-accent transition-all disabled:opacity-50"
            >
              {uploading ? "Upload…" : imgUrl ? "Changer l'image" : "📷 Choisir une image"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && uploadImg(e.target.files[0])} />
          </div>
        </div>
        <button type="submit" disabled={loading || uploading || !title || !imgUrl} className="sensoria-btn w-full py-3 disabled:opacity-50">
          {loading ? "Redirection vers Stripe…" : "Payer et lancer la diffusion →"}
        </button>
        <p className="text-center text-[10px] text-sensoria-muted">
          Paiement sécurisé par Stripe · Votre annonce sera activée dès confirmation · <Link to="/p/conditions-generales-utilisation" className="text-sensoria-accent hover:underline">CGU</Link>
        </p>
      </form>
    </div>
  );
}

// ── Composant principal exporté ─────────────────────────────────────────────
/**
 * variant: "horizontal" | "square" | "sidebar"
 * showLabel: affiche ou non le libellé "Espace partenaire" au-dessus
 */
export default function AdBanner({ variant = "horizontal", showLabel = false }) {
  const [ads, setAds] = useState([]);
  const [index, setIndex] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const token = localStorage.getItem("sensoria_token");

  useEffect(() => {
    fetch(`${API}/api/ads/active`)
      .then(r => r.ok ? r.json() : { ads: [] })
      .then(data => setAds(data.ads || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % ads.length), AD_INTERVAL);
    return () => clearInterval(t);
  }, [ads.length]);

  const handleShowForm = (e) => {
    e?.stopPropagation();
    if (!token) return; // non connecté → silencieux (ou on pourrait afficher un message)
    setShowForm(v => !v);
  };

  return (
    <div className={variant === "horizontal" ? "my-3" : ""}>
      {showLabel && (
        <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-sensoria-border/60">
          Espace partenaire
        </p>
      )}
      {ads.length === 0
        ? <AdPlaceholder variant={variant} onShowForm={handleShowForm} />
        : <AdCard ad={ads[index]} ads={ads} index={index} onShowForm={handleShowForm} variant={variant} />
      }
      {showForm && <AdCheckoutForm token={token} onClose={() => setShowForm(false)} />}
    </div>
  );
}
